/**
 * Format a decimal string amount for display with 2 decimal places.
 * Uses string-based input per C-04 (never JavaScript `number` for storage).
 * parseFloat is used only for locale formatting — the source of truth is the string.
 */
export function formatAmount(value: string, currency?: string): string {
  const num = parseFloat(value);
  if (isNaN(num)) return currency ? `0.00 ${currency}` : '0.00';
  const formatted = num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return currency ? `${formatted} ${currency}` : formatted;
}

/**
 * Format a date string for table display (e.g., "01 Apr 2026").
 */
export function formatDate(isoDate: string): string {
  const d = new Date(isoDate);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Build FX trade description: "Sell X,XXX USD @ 0.92 -> Buy Y,YYY EUR"
 * Format A as resolved in spec §5.
 */
export function formatFxDescription(
  amount: string,
  currency: string,
  rate?: string,
  referenceCurrency?: string,
): string {
  const sellFormatted = formatAmount(amount);
  if (!rate || !referenceCurrency) {
    return `${sellFormatted} ${currency}`;
  }

  const rateNum = parseFloat(rate);
  const amountNum = parseFloat(amount);
  if (isNaN(rateNum) || isNaN(amountNum)) {
    return `${sellFormatted} ${currency}`;
  }

  const buyAmount = (amountNum * rateNum).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return `Sell ${sellFormatted} ${currency} @ ${rate} \u2192 Buy ${buyAmount} ${referenceCurrency}`;
}
